import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, Lock, Plus, ShieldCheck, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { fetchAdminUser, putAdminUser } from '@/lib/api/admin';
import {
  contactPhoneList,
  getAdminProfile,
  isPlatoSynced,
  patientMetaField,
  pick,
} from '@/lib/admin-user-display';
import { PATIENT_TAG_CATALOG } from '@/lib/patient-tags';

function dobToInputValue(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  // Stored DOB is an ISO/RFC3339 timestamp; the date input wants YYYY-MM-DD.
  return s.length >= 10 ? s.slice(0, 10) : s;
}

/**
 * Stored phones as editable rows. The list is edited as a whole and saved through the `phones`
 * channel (a wholesale replace), which is the only one that can rewrite or drop a number — the
 * `contacts` patch merges append-by-E164 and can therefore only ever add.
 */
function phoneRowsFromUser(user) {
  return contactPhoneList(user?.contacts).map((p) => ({
    number: String(pick(p, 'e164', 'E164') ?? '').trim(),
    label: String(pick(p, 'label', 'Label') ?? '').trim(),
  }));
}

function fromUser(user) {
  const p = getAdminProfile(user) || {};
  const pm = user?.patientMeta || {};
  return {
    displayName: String(pick(p, 'displayName', 'DisplayName') ?? '').trim(),
    firstName: String(pick(p, 'firstName', 'FirstName') ?? '').trim(),
    lastName: String(pick(p, 'lastName', 'LastName') ?? '').trim(),
    sex: String(pick(p, 'sex', 'Sex') ?? '').trim(),
    dob: dobToInputValue(pick(p, 'dob', 'DOB')),
    // Login id is a top-level, staff-managed credential (not owned by Plato). Patients created via
    // the admin path / Plato get a synthetic-or-empty loginId; staff can set a real one here.
    loginId: String(pick(user || {}, 'loginId', 'LoginID') ?? '').trim(),
    phones: phoneRowsFromUser(user),
    tags: Array.isArray(pm.tags) ? pm.tags.map((t) => String(t || '').trim()).filter(Boolean) : [],
    localNotes: String(patientMetaField(pm, 'localNotes') || ''),
  };
}

function sameTags(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// Rows are compared as typed (trimmed): reordering or relabelling a number is an edit too, so a
// set comparison would miss changes the save does apply.
function samePhones(a, b) {
  if (a.length !== b.length) return false;
  return a.every((row, i) => row.number.trim() === b[i].number.trim() && row.label.trim() === b[i].label.trim());
}

function ReadOnlyRow({ label, value }) {
  return (
    <div className="space-y-1">
      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Lock className="h-3 w-3" /> {label}
      </span>
      <p className="text-sm text-foreground">{value && String(value).trim() ? value : '—'}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/**
 * Shared patient editor used by BOTH the Patients page and the Messages patient window. It loads
 * the full admin user document by id, shows a "Synced from Plato" badge when applicable, and
 * restricts the editable surface accordingly:
 *   - Plato-synced: only patient tags + local notes are editable; identity/clinical fields are
 *     read-only (the backend rejects edits to the rest as a backstop).
 *   - Native: name / DOB / sex are editable, and the phone list can be edited (add / rewrite /
 *     remove) — it is saved as a wholesale replace, the only channel that can do more than append.
 * On a successful save it calls `onSaved` so the caller can refresh its own view (the Patients
 * list re-fetches; Messages re-loads its member snapshots).
 */
export function PatientEditorDialog({ open, onOpenChange, userId, onSaved, readOnly = false }) {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(() => fromUser(null));
  // Password is write-only: it is never returned by the API, so the field starts blank and an
  // empty value means "leave the current password unchanged".
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const id = String(userId || '').trim();

  useEffect(() => {
    if (!open || !id) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setPassword('');
    setShowPassword(false);
    fetchAdminUser({ userId: id })
      .then((doc) => {
        if (cancelled) return;
        setUser(doc);
        setForm(fromUser(doc));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Could not load patient.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, id]);

  const synced = isPlatoSynced(user);
  const original = useMemo(() => fromUser(user), [user]);
  const phones = contactPhoneList(user?.contacts);
  // Verification is server-owned and survives a replace only for numbers that come back unchanged,
  // so the badge is shown on exactly those rows.
  const verifiedNumbers = useMemo(
    () =>
      new Set(
        contactPhoneList(user?.contacts)
          .filter((p) => pick(p, 'verified', 'Verified'))
          .map((p) => String(pick(p, 'e164', 'E164') ?? '').trim())
          .filter(Boolean),
      ),
    [user],
  );

  const setField = (name) => (event) => {
    if (readOnly) return;
    const value = event?.target?.value ?? event;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleTag = (key) => {
    if (readOnly) return;
    setForm((current) => {
      const has = current.tags.includes(key);
      return {
        ...current,
        tags: has ? current.tags.filter((t) => t !== key) : [...current.tags, key],
      };
    });
  };

  const updatePhone = (index, partial) => {
    if (readOnly) return;
    setForm((current) => ({
      ...current,
      phones: current.phones.map((row, i) => (i === index ? { ...row, ...partial } : row)),
    }));
  };

  const removePhone = (index) => {
    if (readOnly) return;
    setForm((current) => ({ ...current, phones: current.phones.filter((_, i) => i !== index) }));
  };

  const addPhoneRow = () => {
    if (readOnly) return;
    setForm((current) => ({ ...current, phones: [...current.phones, { number: '', label: '' }] }));
  };

  // Login credentials are editable in BOTH synced and native modes (Plato does not own them).
  // Compare loginId case-insensitively since the backend normalizes it to lowercase.
  const loginIdChanged = useMemo(
    () => form.loginId.trim().toLowerCase() !== original.loginId.trim().toLowerCase(),
    [form.loginId, original.loginId],
  );

  const hasChanges = useMemo(() => {
    if (!user) return false;
    if (!sameTags(form.tags, original.tags)) return true;
    if (form.localNotes.trim() !== original.localNotes.trim()) return true;
    if (loginIdChanged) return true;
    if (password) return true;
    if (!synced) {
      if (form.displayName.trim() !== original.displayName) return true;
      if (form.firstName.trim() !== original.firstName) return true;
      if (form.lastName.trim() !== original.lastName) return true;
      if (form.sex.trim() !== original.sex) return true;
      if (form.dob.trim() !== original.dob) return true;
      if (!samePhones(form.phones, original.phones)) return true;
    }
    return false;
  }, [user, form, original, synced, loginIdChanged, password]);

  const handleSave = async () => {
    if (!user || !hasChanges || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const patch = { userId: id };

      // Tags + local notes are editable in both modes (patientMeta deep-merges; the tags array
      // replaces wholesale, which is what we want).
      if (!sameTags(form.tags, original.tags) || form.localNotes.trim() !== original.localNotes.trim()) {
        patch.patientMeta = { tags: form.tags, localNotes: form.localNotes.trim() };
      }

      // Login credentials are editable in both modes (Plato does not own them). Password is
      // write-only, so a non-empty value rotates it and an empty value leaves it untouched.
      if (loginIdChanged) {
        const nextLogin = form.loginId.trim();
        if (!nextLogin) {
          setError('Login ID cannot be empty.');
          return;
        }
        patch.loginId = nextLogin;
      }
      if (password) {
        patch.password = password;
      }

      if (!synced) {
        // Profile is field-merged server-side (empty fields are ignored), so send only changed,
        // non-empty fields, using the PascalCase keys the Go struct expects.
        const profile = {};
        if (form.displayName.trim() && form.displayName.trim() !== original.displayName) profile.DisplayName = form.displayName.trim();
        if (form.firstName.trim() && form.firstName.trim() !== original.firstName) profile.FirstName = form.firstName.trim();
        if (form.lastName.trim() && form.lastName.trim() !== original.lastName) profile.LastName = form.lastName.trim();
        if (form.sex.trim() && form.sex.trim() !== original.sex) profile.Sex = form.sex.trim();
        if (form.dob.trim() && form.dob.trim() !== original.dob) {
          // Date input gives YYYY-MM-DD; the backend wants an RFC3339 timestamp.
          profile.DOB = new Date(`${form.dob.trim()}T00:00:00.000Z`).toISOString();
        }
        if (Object.keys(profile).length > 0) patch.profile = profile;

        // Phones replace the stored list wholesale (the append-only `contacts` merge cannot
        // rewrite or delete a number). Blank rows are dropped server-side, so an untouched "add"
        // row never blocks the save; an emptied list clears every number.
        if (!samePhones(form.phones, original.phones)) {
          patch.phones = form.phones.map((row) => ({ number: row.number.trim(), label: row.label.trim() }));
        }
      }

      await putAdminUser(patch);
      toast.success('Patient updated.');
      onSaved?.(id);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || 'Could not save patient.');
    } finally {
      setIsSaving(false);
    }
  };

  const headerName = form.displayName || original.displayName || [form.firstName, form.lastName].filter(Boolean).join(' ') || 'Patient';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {headerName}
            {synced && (
              <Badge variant="secondary" className="gap-1 border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-950/60 dark:text-cyan-300">
                <ShieldCheck className="h-3 w-3" /> Synced from Plato
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {readOnly
              ? 'View-only: your access level doesn’t allow editing patients.'
              : synced
                ? 'This patient is synced from Plato. Tags, notes, and login access can be edited here.'
                : 'Edit this patient’s details, tags, notes, and login access.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <ScrollArea viewportClassName="max-h-[60vh]">
          <div className="space-y-5 px-0.5 py-1 pr-3">
            {/* Identity */}
            <div className="space-y-3">
              {synced || readOnly ? (
                <>
                  <ReadOnlyRow label="Name" value={original.displayName || [original.firstName, original.lastName].filter(Boolean).join(' ')} />
                  <div className="grid grid-cols-2 gap-3">
                    <ReadOnlyRow label="Date of birth" value={original.dob} />
                    <ReadOnlyRow label="Sex" value={original.sex} />
                  </div>
                </>
              ) : (
                <>
                  <Field label="Display name">
                    <Input value={form.displayName} onChange={setField('displayName')} placeholder="e.g. Jane Tan" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="First name">
                      <Input value={form.firstName} onChange={setField('firstName')} />
                    </Field>
                    <Field label="Last name">
                      <Input value={form.lastName} onChange={setField('lastName')} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Date of birth">
                      <Input type="date" value={form.dob} onChange={setField('dob')} />
                    </Field>
                    <Field label="Sex">
                      <Input value={form.sex} onChange={setField('sex')} placeholder="M / F" />
                    </Field>
                  </div>
                </>
              )}
            </div>

            {/* Phones */}
            <div className="space-y-2">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                {synced && <Lock className="h-3 w-3" />} Phones
              </span>
              {synced || readOnly ? (
                phones.length > 0 ? (
                  <ul className="space-y-1">
                    {phones.map((p, i) => {
                      const e164 = pick(p, 'e164', 'E164');
                      const verified = pick(p, 'verified', 'Verified');
                      return (
                        <li key={`${e164}-${i}`} className="flex items-center justify-between rounded-md border border-border/60 px-2.5 py-1.5">
                          <span className="font-mono text-xs text-foreground">{e164 || '—'}</span>
                          {verified ? (
                            <Badge className="border-none bg-emerald-500/10 text-[9px] text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">Verified</Badge>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">No phone numbers on file.</p>
                )
              ) : (
                <>
                  {form.phones.map((row, i) => (
                    // Index keys are safe here: the rows are fully controlled, so a removal
                    // re-renders the survivors with their own values.
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={row.number}
                        onChange={(e) => updatePhone(i, { number: e.target.value })}
                        placeholder="+6591234567"
                        inputMode="tel"
                        className="flex-1 font-mono text-xs"
                        aria-label={`Phone number ${i + 1}`}
                      />
                      <Input
                        value={row.label}
                        onChange={(e) => updatePhone(i, { label: e.target.value })}
                        placeholder="Label"
                        className="w-24 text-xs"
                        aria-label={`Phone label ${i + 1}`}
                      />
                      {/* A fixed slot, present on every row once any number is verified, so the
                          inputs above and below it still line up. */}
                      {verifiedNumbers.size > 0 && (
                        <span className="w-14 shrink-0 text-center">
                          {verifiedNumbers.has(row.number.trim()) ? (
                            <Badge className="border-none bg-emerald-500/10 text-[9px] text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">Verified</Badge>
                          ) : null}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removePhone(i)}
                        aria-label={`Remove phone ${i + 1}`}
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  ))}
                  {form.phones.length === 0 && (
                    <p className="text-xs text-muted-foreground">No phone numbers on file.</p>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addPhoneRow}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Add phone
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    Saving replaces the whole list, so an edited number is rewritten in place and a removed one is
                    dropped. Existing chat threads stay attached to the patient, not to the number.
                  </p>
                </>
              )}
            </div>

            {/* Tags (editable in both modes) */}
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground">Tags</span>
              <div className="flex flex-wrap gap-1.5">
                {PATIENT_TAG_CATALOG.map((def) => {
                  const active = form.tags.includes(def.key);
                  const Icon = def.icon;
                  return (
                    <button
                      key={def.key}
                      type="button"
                      onClick={() => toggleTag(def.key)}
                      title={def.description}
                      disabled={readOnly}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors',
                        active ? def.chipClassName : 'border-border bg-transparent text-muted-foreground hover:bg-muted',
                        readOnly && 'cursor-default opacity-70 hover:bg-transparent',
                      )}
                    >
                      <Icon className={cn('h-3 w-3', active ? def.glyphClassName : '')} />
                      {def.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Local notes (editable in both modes) */}
            <Field label="Notes (local — not synced to Plato)">
              <textarea
                value={form.localNotes}
                onChange={setField('localNotes')}
                rows={4}
                disabled={readOnly}
                placeholder="Staff-only notes about this patient…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
              />
            </Field>

            {/* Login access (credentials — editable in both modes; Plato does not own them) */}
            <div className="space-y-3 border-t border-border/60 pt-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <KeyRound className="h-3 w-3" /> Login access
              </span>
              {readOnly ? (
                <ReadOnlyRow label="Login ID" value={original.loginId} />
              ) : (
                <>
                  <Field label="Login ID">
                    <Input
                      value={form.loginId}
                      onChange={setField('loginId')}
                      placeholder="e.g. jane.tan@example.com"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="New password">
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Leave blank to keep current password"
                        autoComplete="new-password"
                        className="pr-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </Field>
                  <p className="text-[11px] text-muted-foreground">
                    Patients don’t need a login to be messaged — set these only if this patient signs in. The password is write-only; leave it blank to keep the current one.
                  </p>
                </>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} disabled={isLoading || isSaving || !hasChanges}>
              {isSaving ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PatientEditorDialog;
